import { supabase } from '../supabaseClient';
import {
  parseBillingPaymentMode,
  type BillingPaymentMode,
  type PlatformBillingSettings,
} from '../types/billingSettings';

const CACHE_PAID_KEY = 'paidPaymentEnabled';
const CACHE_MODE_KEY = 'paymentMode';

const DEFAULT_SETTINGS: PlatformBillingSettings = {
  paid_payment_enabled: false,
  payment_mode: 'test',
};

function mapRow(row: Record<string, unknown> | null | undefined): PlatformBillingSettings {
  if (!row) return { ...DEFAULT_SETTINGS };
  return {
    paid_payment_enabled: Boolean(row.paid_payment_enabled),
    payment_mode: parseBillingPaymentMode(row.payment_mode),
    updated_at: typeof row.updated_at === 'string' ? row.updated_at : undefined,
  };
}

export function loadCachedBillingSettings(): PlatformBillingSettings {
  try {
    const paid = localStorage.getItem(CACHE_PAID_KEY);
    const mode = localStorage.getItem(CACHE_MODE_KEY);
    return {
      paid_payment_enabled: paid === 'true',
      payment_mode: parseBillingPaymentMode(mode),
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveBillingSettingsCache(settings: PlatformBillingSettings): void {
  try {
    localStorage.setItem(CACHE_PAID_KEY, settings.paid_payment_enabled ? 'true' : 'false');
    localStorage.setItem(CACHE_MODE_KEY, settings.payment_mode);
  } catch {
    /* ignore */
  }
}

export async function getBillingSettings(): Promise<PlatformBillingSettings> {
  const { data, error } = await supabase
    .from('platform_billing_settings')
    .select('paid_payment_enabled, payment_mode, updated_at')
    .eq('id', 'default')
    .maybeSingle();

  if (error) throw error;
  const settings = mapRow(data as Record<string, unknown> | null);
  saveBillingSettingsCache(settings);
  return settings;
}

async function upsertBillingPatch(
  patch: Partial<Pick<PlatformBillingSettings, 'paid_payment_enabled' | 'payment_mode'>>,
): Promise<PlatformBillingSettings> {
  const { data, error } = await supabase
    .from('platform_billing_settings')
    .upsert(
      {
        id: 'default',
        ...patch,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'id' },
    )
    .select('paid_payment_enabled, payment_mode, updated_at')
    .single();

  if (error) throw error;
  const settings = mapRow(data as Record<string, unknown>);
  saveBillingSettingsCache(settings);
  return settings;
}

export async function setPaidPaymentEnabled(enabled: boolean): Promise<PlatformBillingSettings> {
  return upsertBillingPatch({ paid_payment_enabled: enabled });
}

export async function setPaymentMode(mode: BillingPaymentMode): Promise<PlatformBillingSettings> {
  return upsertBillingPatch({ payment_mode: mode });
}
